<script setup>
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/vue";
import {
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
} from "@heroicons/vue/24/solid";
import { getDatesCookies, setCookie } from "@js/utils/cookies";
import { isDifferentDates, sameDayPreviousYear } from "@js/utils/date";
import { formatDateForCookies } from "@js/utils/formatter";
import { breakpointsTailwind, useWindowSize } from "@vueuse/core";
import { DatePicker } from "v-calendar";
import "v-calendar/style.css";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const CURRENT_DATE_RANGE_KEY = "currentDateRange";

const CURRENT_DATE_RANGE_TO_COMPARE_KEY = "currentDateRangeToCompare";

const {
  callback = () => {},
  compare = false,
  comparablePeriods = ["custom", "previousPeriod", "previousYear"],
  canSelectToday = false,
  reverse = false,
} = defineProps({
  callback: {
    type: Function,
    required: false,
  },
  compare: {
    type: Boolean,
    required: false,
  },
  comparablePeriods: {
    type: Array,
    required: false,
  },
  canSelectToday: {
    type: Boolean,
    required: false,
  },
  reverse: {
    type: Boolean,
    required: false,
  },
});

const { width, height } = useWindowSize();

const { t } = useI18n();

const getCurrentFormattedDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

onMounted(() => {
  const initialDates = getDatesCookies();

  period.value = initialDates.period;
  currentPeriod.value = initialDates.period;

  dateRange.value = {
    start: initialDates.startDate,
    end: initialDates.endDate,
  };

  currentDateRange.value = {
    start: initialDates.startDate,
    end: initialDates.endDate,
  };

  if (canBeCompared) {
    periodToCompare.value = initialDates.periodToCompare;
    currentPeriodToCompare.value = initialDates.periodToCompare;

    dateRangeToCompare.value = {
      start: initialDates.startDateToCompare,
      end: initialDates.endDateToCompare,
    };

    currentDateRangeToCompare.value = {
      start: initialDates.startDateToCompare,
      end: initialDates.endDateToCompare,
    };

    currentDateRangeAttributes.value[0].dates[0].start =
      currentDateRangeToCompare.value.start;
    currentDateRangeAttributes.value[0].dates[0].end =
      currentDateRangeToCompare.value.end;

    currentDateRangeToCompareAttributes.value[0].dates[0].start =
      currentDateRange.value.start;
    currentDateRangeToCompareAttributes.value[0].dates[0].end =
      currentDateRange.value.end;
  }

  // Check if we can select today
  if (!canSelectToday) {
    // Check if current date range is including today
    // If it's the case, we set the current date range to the previous day and update the cookies
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (currentDateRange.value.end === getCurrentFormattedDate()) {
      currentDateRange.value.end = new Date(today.setDate(today.getDate() - 1));
      if (currentDateRange.value.start === getCurrentFormattedDate()) {
        currentDateRange.value.start = new Date(today);
      }
      update(() => {});
    }
  }

  currentDatePicker.value = CURRENT_DATE_RANGE_KEY;
});

const PERIODS = {
  custom: {
    value: "custom",
    label: t("all.label.custom"),
  },
  yesterday: {
    value: "yesterday",
    label: t("all.label.yesterday"),
  },
  beforeYesterday: {
    value: "beforeYesterday",
    label: t("all.label.beforeYesterday"),
  },
  last7Days: {
    value: "last7Days",
    label: t("all.label.last7Days"),
  },
  last30Days: {
    value: "last30Days",
    label: t("all.label.last30Days"),
  },
  last90Days: {
    value: "last90Days",
    label: t("all.label.last90Days"),
  },
  thisWeek: {
    value: "thisWeek",
    label: t("all.label.thisWeek"),
  },
  lastWeek: {
    value: "lastWeek",
    label: t("all.label.lastWeek"),
  },
  thisMonth: {
    value: "thisMonth",
    label: t("all.label.thisMonth"),
  },
  lastMonth: {
    value: "lastMonth",
    label: t("all.label.lastMonth"),
  },
  thisYear: {
    value: "thisYear",
    label: t("all.label.thisYear"),
  },
  lastYear: {
    value: "lastYear",
    label: t("all.label.lastYear"),
  },
};

const PERIODS_TO_COMPARE = {
  custom: {
    value: "custom",
    label: t("all.label.custom"),
  },
  previousYear: {
    value: "previousYear",
    label: t("all.label.previousYear"),
  },
  previousYearWithSameDate: {
    value: "previousYearWithSameDate",
    label: t("all.label.previousYearWithSameDate"),
  },
  previousPeriod: {
    value: "previousPeriod",
    label: t("all.label.previousPeriod"),
  },
};

const lastWeekDate = new Date();
lastWeekDate.setDate(lastWeekDate.getDate() - 7);

const yesterdayDate = new Date();
yesterdayDate.setDate(yesterdayDate.getDate() - 1);

const dateRange = ref({
  start: lastWeekDate,
  end: yesterdayDate,
});

const period = ref("custom");

const lastYearWeekDate = new Date();
lastYearWeekDate.setDate(lastYearWeekDate.getDate() - 7);
lastYearWeekDate.setFullYear(lastYearWeekDate.getFullYear() - 1);

const yesterdayYearDate = new Date();
yesterdayYearDate.setDate(yesterdayYearDate.getDate() - 1);
yesterdayYearDate.setFullYear(yesterdayYearDate.getFullYear() - 1);

const dateRangeToCompare = ref({
  start: lastYearWeekDate,
  end: yesterdayYearDate,
});

const periodToCompare = ref(
  comparablePeriods.includes("previousYear")
    ? "previousYear"
    : comparablePeriods[0],
);

const currentDateRange = ref(dateRange.value);

const currentDateRangeToCompare = ref(dateRangeToCompare.value);

const currentPeriod = ref(period.value);

const currentPeriodToCompare = ref(periodToCompare.value);

const currentDateRangeAttributes = ref([
  {
    key: CURRENT_DATE_RANGE_TO_COMPARE_KEY,
    highlight: "orange",
    dates: [
      {
        start: dateRangeToCompare.value.start,
        end: dateRangeToCompare.value.end,
      },
    ],
  },
]);

const currentDateRangeToCompareAttributes = ref([
  {
    key: CURRENT_DATE_RANGE_KEY,
    highlight: "blue",
    dates: [
      {
        start: dateRange.value.start,
        end: dateRange.value.end,
      },
    ],
  },
]);

const key = ref(0);

const currentDatePicker = ref(CURRENT_DATE_RANGE_KEY);

const maxDate = computed(() => {
  let maxDate = new Date();
  maxDate.setDate(maxDate.getDate() - (canSelectToday ? 0 : 1));
  return maxDate;
});

const columns = computed(() => {
  if (width.value < breakpointsTailwind.lg) {
    return 1;
  }

  if (width.value < breakpointsTailwind.xl) {
    return 2;
  }

  return 3;
});

const hasDatesChanged = computed(
  () =>
    period.value !== currentPeriod.value ||
    periodToCompare.value !== currentPeriodToCompare.value ||
    isDifferentDates(dateRange.value.start, currentDateRange.value.start) ||
    isDifferentDates(dateRange.value.end, currentDateRange.value.end) ||
    isDifferentDates(
      dateRangeToCompare.value.start,
      currentDateRangeToCompare.value.start,
    ) ||
    isDifferentDates(
      dateRangeToCompare.value.end,
      currentDateRangeToCompare.value.end,
    ),
);

const formattedDateRange = computed(() => {
  return (
    formatDate(currentDateRange.value.start) +
    " - " +
    formatDate(currentDateRange.value.end)
  );
});

const formattedDateRangeToCompare = computed(() => {
  return (
    formatDate(currentDateRangeToCompare.value.start) +
    " - " +
    formatDate(currentDateRangeToCompare.value.end)
  );
});

const canBeCompared = computed(() => {
  return compare && dateRangeToCompare.value && periodToCompare.value;
});

const formattedCurrentDateRange = computed(() => {
  return (
    formatDate(currentDateRange.value.start) +
    " - " +
    formatDate(currentDateRange.value.end)
  );
});

const formattedCurrentDateRangeToCompare = computed(() => {
  return (
    formatDate(currentDateRangeToCompare.value.start) +
    " - " +
    formatDate(currentDateRangeToCompare.value.end)
  );
});

function formatDate(date) {
  if (typeof date == "string") {
    date = new Date(date);
  }
  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function handleCurrentDateRange() {
  currentPeriod.value = "custom";

  currentDateRangeToCompareAttributes.value[0].dates[0].start =
    currentDateRange.value.start;
  currentDateRangeToCompareAttributes.value[0].dates[0].end =
    currentDateRange.value.end;

  if (canBeCompared && currentPeriodToCompare.value !== "custom") {
    handlePeriodToCompare();
  } else {
    key.value++;
  }
}

function handleCurrentDateRangeToCompare() {
  currentDateRangeAttributes.value[0].dates[0].start =
    currentDateRangeToCompare.value.start;
  currentDateRangeAttributes.value[0].dates[0].end =
    currentDateRangeToCompare.value.end;

  key.value++;
}

function handlePeriod() {
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);

  const beforeYesterdayDate = new Date();
  beforeYesterdayDate.setDate(beforeYesterdayDate.getDate() - 2);

  const last7DaysDate = new Date();
  last7DaysDate.setDate(last7DaysDate.getDate() - 7);

  const last30DaysDate = new Date();
  last30DaysDate.setMonth(last30DaysDate.getMonth() - 1);
  last30DaysDate.setDate(last30DaysDate.getDate() + 1);

  const last90DaysDate = new Date();
  last90DaysDate.setMonth(last90DaysDate.getMonth() - 3);
  last90DaysDate.setDate(last90DaysDate.getDate() + 1);

  const thisWeekStart = new Date();
  thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());
  thisWeekStart.setDate(thisWeekStart.getDate() + 1);

  const lastWeekStartDate = new Date();
  lastWeekStartDate.setDate(
    lastWeekStartDate.getDate() - lastWeekStartDate.getDay() - 7,
  );
  lastWeekStartDate.setDate(lastWeekStartDate.getDate() + 1);

  const lastWeekEndDate = new Date();
  lastWeekEndDate.setDate(
    lastWeekEndDate.getDate() - lastWeekEndDate.getDay() - 1,
  );
  lastWeekEndDate.setDate(lastWeekEndDate.getDate() + 1);

  const thisMonthStartDate = new Date();
  thisMonthStartDate.setDate(1);

  const lastMonthStartDate = new Date();
  lastMonthStartDate.setMonth(lastMonthStartDate.getMonth() - 1);
  lastMonthStartDate.setDate(1);

  const lastMonthEndDate = new Date();
  lastMonthEndDate.setDate(0);

  const thisYearStartDate = new Date();
  thisYearStartDate.setMonth(0);
  thisYearStartDate.setDate(1);

  const lastYearStartDate = new Date();
  lastYearStartDate.setFullYear(lastYearStartDate.getFullYear() - 1);
  lastYearStartDate.setMonth(0);
  lastYearStartDate.setDate(1);

  const lastYearEndDate = new Date();
  lastYearEndDate.setFullYear(lastYearEndDate.getFullYear() - 1);
  lastYearEndDate.setMonth(11);
  lastYearEndDate.setDate(31);

  switch (currentPeriod.value) {
    case "yesterday":
      currentDateRange.value.start = yesterdayDate;
      currentDateRange.value.end = yesterdayDate;
      break;
    case "beforeYesterday":
      currentDateRange.value.start = beforeYesterdayDate;
      currentDateRange.value.end = beforeYesterdayDate;
      break;
    case "last7Days":
      currentDateRange.value.start = last7DaysDate;
      currentDateRange.value.end = yesterdayDate;
      break;
    case "last30Days":
      currentDateRange.value.start = last30DaysDate;
      currentDateRange.value.end = yesterdayDate;
      break;
    case "last90Days":
      currentDateRange.value.start = last90DaysDate;
      currentDateRange.value.end = yesterdayDate;
      break;
    case "thisWeek":
      currentDateRange.value.start = thisWeekStart;
      currentDateRange.value.end = yesterdayDate;
      break;
    case "lastWeek":
      currentDateRange.value.start = lastWeekStartDate;
      currentDateRange.value.end = lastWeekEndDate;
      break;
    case "thisMonth":
      currentDateRange.value.start = thisMonthStartDate;
      currentDateRange.value.end = yesterdayDate;
      break;
    case "lastMonth":
      currentDateRange.value.start = lastMonthStartDate;
      currentDateRange.value.end = lastMonthEndDate;
      break;
    case "thisYear":
      currentDateRange.value.start = thisYearStartDate;
      currentDateRange.value.end = yesterdayDate;
      break;
    case "lastYear":
      currentDateRange.value.start = lastYearStartDate;
      currentDateRange.value.end = lastYearEndDate;
      break;
  }

  currentDateRangeToCompareAttributes.value[0].dates[0].start =
    currentDateRange.value.start;
  currentDateRangeToCompareAttributes.value[0].dates[0].end =
    currentDateRange.value.end;

  if (canBeCompared) {
    handlePeriodToCompare();
  } else {
    key.value++;
  }
}

function handlePeriodToCompare() {
  let start = new Date(currentDateRange.value.start);
  let end = new Date(currentDateRange.value.end);

  let diff = end.getTime() - start.getTime();

  switch (currentPeriodToCompare.value) {
    case "custom":
      start = new Date(start.getTime() - diff);
      start.setDate(start.getDate() - 1);
      end = new Date(end.getTime() - diff);
      end.setDate(end.getDate() - 1);

      currentDateRangeToCompare.value.start = start;
      currentDateRangeToCompare.value.end = end;

      currentDatePicker.value = CURRENT_DATE_RANGE_TO_COMPARE_KEY;

      break;
    case "previousYear":
      start.setFullYear(start.getFullYear() - 1);
      end.setFullYear(end.getFullYear() - 1);

      currentDateRangeToCompare.value.start = start;
      currentDateRangeToCompare.value.end = end;

      currentDatePicker.value = CURRENT_DATE_RANGE_KEY;

      break;
    case "previousYearWithSameDate":
      start = sameDayPreviousYear(start);
      end = sameDayPreviousYear(end);

      currentDateRangeToCompare.value.start = start;
      currentDateRangeToCompare.value.end = end;

      currentDatePicker.value = CURRENT_DATE_RANGE_KEY;

      break;
    case "previousPeriod":
      start = new Date(start.getTime() - diff);
      start.setDate(start.getDate() - 1);
      end = new Date(end.getTime() - diff);
      end.setDate(end.getDate() - 1);

      currentDateRangeToCompare.value.start = start;
      currentDateRangeToCompare.value.end = end;

      currentDatePicker.value = CURRENT_DATE_RANGE_KEY;

      break;
  }

  currentDateRangeAttributes.value[0].dates[0].start =
    currentDateRangeToCompare.value.start;
  currentDateRangeAttributes.value[0].dates[0].end =
    currentDateRangeToCompare.value.end;

  key.value++;
}

function setCurrentDatePicker(value) {
  if (currentPeriodToCompare.value === "custom") {
    currentDatePicker.value = value;
    key.value++;
  }
}

function update(closePopover) {
  setCookie("startDate", formatDateForCookies(currentDateRange.value.start));
  setCookie("endDate", formatDateForCookies(currentDateRange.value.end));
  setCookie(
    "startDateToCompare",
    formatDateForCookies(currentDateRangeToCompare.value.start),
  );
  setCookie(
    "endDateToCompare",
    formatDateForCookies(currentDateRangeToCompare.value.end),
  );
  setCookie("period", currentPeriod.value);
  setCookie("periodToCompare", currentPeriodToCompare.value);
  callback();
  closePopover();
}
</script>

<template>
  <Popover v-slot="{ open: openPopover, close: closePopover }" class="relative">
    <PopoverButton
      class="flex items-center rounded bg-indigo-600 px-3 py-1 text-sm text-white duration-150 hover:bg-indigo-950 focus:outline-none"
    >
      <div class="text-left">
        <h3>
          <strong>{{ hasDatesChanged ? "*" : "" }}</strong>
          {{ formattedDateRange }}
        </h3>

        <h3 v-if="canBeCompared" class="mt-1 text-xs">
          {{
            t("all.button.compareTo", {
              to: formattedDateRangeToCompare,
            })
          }}
        </h3>
      </div>

      <ChevronUpIcon
        v-if="reverse"
        class="ml-2 size-4 duration-150"
        :class="{ '-rotate-180': openPopover }"
      />

      <ChevronDownIcon
        v-else
        class="ml-2 size-4 duration-150"
        :class="{ 'rotate-180': openPopover }"
      />
    </PopoverButton>

    <PopoverPanel
      class="absolute left-0 z-40 flex flex-col items-center rounded border border-white bg-white px-4 py-6 drop-shadow-md md:left-auto md:right-0 md:flex-row"
      :class="{
        'top-10': !canBeCompared && !reverse,
        'top-14': canBeCompared && !reverse,
        'bottom-10': !canBeCompared && reverse,
        'bottom-14': canBeCompared && reverse,
      }"
    >
      <DatePicker
        v-if="currentDatePicker === CURRENT_DATE_RANGE_KEY"
        v-model.range="currentDateRange"
        :attributes="canBeCompared && currentDateRangeAttributes"
        :max-date="maxDate"
        :columns="columns"
        :key="key"
        @update:modelValue="handleCurrentDateRange"
        color="blue"
        borderless
        title-position="left"
        class="date-range-picker-calendar"
      />
      <DatePicker
        v-if="
          canBeCompared &&
          currentDatePicker === CURRENT_DATE_RANGE_TO_COMPARE_KEY
        "
        v-model.range="currentDateRangeToCompare"
        :attributes="currentDateRangeToCompareAttributes"
        :max-date="maxDate"
        :columns="columns"
        :key="key"
        @update:modelValue="handleCurrentDateRangeToCompare"
        color="orange"
        borderless
        title-position="left"
        class="date-range-picker-calendar"
      />

      <div
        class="mt-4 w-full border-t border-gray-200 pt-4 md:ml-4 md:mt-0 md:border-l md:border-t-0 md:pl-4 md:pt-0"
      >
        <Listbox
          v-model="currentPeriod"
          v-slot="{ open: openCurrentPeriod }"
          class="relative flex flex-col"
          as="div"
          @update:modelValue="handlePeriod"
        >
          <ListboxLabel class="mb-2 font-medium">
            {{ t("all.label.period") }}
          </ListboxLabel>

          <ListboxButton
            class="mb-4 flex items-center justify-between rounded border border-gray-600 px-4 py-2 text-left duration-150 hover:bg-indigo-600 hover:text-white"
            :class="{
              'bg-indigo-600 text-white': openCurrentPeriod,
            }"
          >
            {{ PERIODS[currentPeriod].label }}

            <ChevronUpDownIcon class="ml-2 h-4 w-4" />
          </ListboxButton>

          <ListboxOptions
            class="absolute left-0 top-20 z-50 w-full rounded bg-white py-2 drop-shadow-md"
          >
            <ListboxOption
              v-for="(period, periodKey) in PERIODS"
              :key="periodKey"
              :value="period.value"
              as="template"
            >
              <li
                class="cursor-pointer px-4 py-2 text-sm duration-150 hover:bg-gray-100"
              >
                {{ period.label }}
              </li>
            </ListboxOption>
          </ListboxOptions>
        </Listbox>

        <button
          class="w-full whitespace-nowrap rounded border border-gray-600 px-4 py-2 text-left"
          :class="{
            'border-inner-blue': currentDatePicker === CURRENT_DATE_RANGE_KEY,
          }"
          @click="setCurrentDatePicker(CURRENT_DATE_RANGE_KEY)"
        >
          {{ formattedCurrentDateRange }}
        </button>

        <Listbox
          :disabled="comparablePeriods.length <= 1"
          v-if="canBeCompared"
          v-slot="{ open: openCurrentPeriodToCompare }"
          v-model="currentPeriodToCompare"
          class="relative mt-4 flex flex-col"
          as="div"
          @update:modelValue="handlePeriodToCompare"
        >
          <ListboxLabel class="mb-2 font-medium"
            >{{ t("all.label.compareTo") }}
          </ListboxLabel>

          <ListboxButton
            class="mb-4 flex items-center justify-between rounded border border-gray-600 px-4 py-2 text-left duration-150 hover:bg-indigo-600 hover:text-white"
            :class="{
              'bg-indigo-600 text-white': openCurrentPeriodToCompare,
            }"
          >
            {{ PERIODS_TO_COMPARE[currentPeriodToCompare].label }}

            <ChevronUpDownIcon class="ml-2 h-4 w-4" />
          </ListboxButton>

          <ListboxOptions
            class="absolute left-0 top-20 z-50 w-full rounded bg-white py-2 drop-shadow-md"
          >
            <ListboxOption
              v-for="(
                periodToCompare, periodToCompareKey
              ) in PERIODS_TO_COMPARE"
              :key="periodToCompareKey"
              :value="periodToCompare.value"
              as="template"
            >
              <li
                class="cursor-pointer px-4 py-2 text-sm duration-150 hover:bg-gray-100"
              >
                {{ periodToCompare.label }}
              </li>
            </ListboxOption>
          </ListboxOptions>
        </Listbox>

        <button
          v-if="canBeCompared"
          class="w-full whitespace-nowrap rounded border border-gray-600 px-4 py-2 text-left"
          :class="{
            'border-inner-orange':
              currentDatePicker === CURRENT_DATE_RANGE_TO_COMPARE_KEY,
            'cursor-auto opacity-70': currentPeriodToCompare !== 'custom',
          }"
          @click="setCurrentDatePicker(CURRENT_DATE_RANGE_TO_COMPARE_KEY)"
        >
          {{ formattedCurrentDateRangeToCompare }}
        </button>

        <button
          :disabled="!hasDatesChanged"
          @click="update(closePopover)"
          class="mt-4 w-full whitespace-nowrap rounded border border-gray-600 bg-indigo-600 px-4 py-2 text-white duration-150 hover:bg-indigo-950 disabled:border-gray-200 disabled:bg-gray-200"
        >
          {{ t("all.button.update") }}
        </button>
      </div>
    </PopoverPanel>
  </Popover>
</template>

<style scoped>
.border-inner-blue {
  border-color: #3182ce !important;
}

.border-inner-orange {
  border-color: #dd6b20 !important;
}
</style>
